import { useEffect, useRef } from 'react'

const W = 320
const CYCLE = 6
const COLS = 8
const LEVELS = 4
const LEVEL_COLORS = ['#39a0ff', '#7a5cff', '#ff5aa0', '#ffb24a']
const GRID_X0 = 22
const COL_STEP = (W - 44) / COLS
const CELL_H = 22
const ROW_STEP = 26
const GRID_Y0 = 70

const OUT_N = 160
const outEnv = (u) => Math.exp(-((u - 0.34) ** 2) / 0.014) + 0.85 * Math.exp(-((u - 0.68) ** 2) / 0.018)

function mix(hex, amt) {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
  return `rgb(${(r * amt) | 0},${(g * amt) | 0},${(b * amt) | 0})`
}

// Modern TTS = recognition in reverse: text becomes discrete neural-codec
// tokens (RVQ codebooks, generated left → right), then a diffusion vocoder
// resolves those tokens from noise into a clean waveform.
export function Scene09TTS2D() {
  const cellRefs = useRef([])
  const cursorRef = useRef()
  const outPathRef = useRef()
  const codes = useRef(new Float32Array(COLS * LEVELS).fill(0.5))
  const cycleId = useRef(-1)
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      const t = (now - start) / 1000
      const phase = t % CYCLE
      const cyc = Math.floor(t / CYCLE)
      if (cyc !== cycleId.current) {
        cycleId.current = cyc
        for (let i = 0; i < codes.current.length; i++) codes.current[i] = Math.random()
      }
      const generated = Math.min(COLS, Math.floor(phase / 0.3))

      cellRefs.current.forEach((el, idx) => {
        if (!el) return
        const c = Math.floor(idx / LEVELS)
        const l = idx % LEVELS
        const vis = c < generated
        el.setAttribute('opacity', vis ? '1' : '0')
        if (vis) {
          const code = codes.current[idx]
          el.setAttribute('fill', mix(LEVEL_COLORS[l], 0.4 + code * 0.6))
          const h = CELL_H * (0.55 + code * 0.45)
          const cy = GRID_Y0 + l * ROW_STEP
          el.setAttribute('y', (cy - h / 2).toFixed(1))
          el.setAttribute('height', h.toFixed(1))
        }
      })

      if (cursorRef.current) {
        const showing = generated < COLS && phase < 2.6
        cursorRef.current.setAttribute('opacity', showing ? '0.9' : '0')
        if (showing) cursorRef.current.setAttribute('x', (GRID_X0 + generated * COL_STEP - 2).toFixed(1))
      }

      const noiseRaw = phase < 2.6 ? 1 : Math.max(0, Math.min(1, 1 - (phase - 2.6) / 2))
      const noise = Math.round(noiseRaw * 4) / 4
      let d = ''
      for (let i = 0; i <= OUT_N; i++) {
        const u = i / OUT_N
        const x = 20 + u * (W - 40)
        const clean = outEnv(u) * Math.sin(x * 0.4 - t * 6) * 30
        const y = 300 + clean * (1 - noise) + (Math.random() - 0.5) * 50 * noise * outEnv(u)
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
      }
      if (outPathRef.current) outPathRef.current.setAttribute('d', d)

      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  return (
    <svg viewBox="0 0 320 360" style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      <text x={W / 2} y="24" fill="#cfe0ec" fontSize="16" fontFamily="system-ui, sans-serif" textAnchor="middle">"hello"</text>
      <text x={W / 2} y="46" fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>
      <text x={W / 2} y="60" fill="#5d7891" fontSize="9" fontFamily="monospace" textAnchor="middle" letterSpacing="0.05em">text → discrete codec tokens</text>

      {Array.from({ length: COLS }).map((_, c) =>
        Array.from({ length: LEVELS }).map((_, l) => (
          <rect
            key={`${c}-${l}`}
            ref={(el) => { cellRefs.current[c * LEVELS + l] = el }}
            x={GRID_X0 + c * COL_STEP - 12} y={GRID_Y0 + l * ROW_STEP - 10}
            width="24" height="20" rx="2"
            fill={LEVEL_COLORS[l]} opacity="0"
          />
        ))
      )}
      <rect ref={cursorRef} y={GRID_Y0 - ROW_STEP / 2 - 6} width="28" height={LEVELS * ROW_STEP} fill="none" stroke="#ffffff" strokeWidth="1.5" opacity="0" />

      <text x={W / 2} y="212" fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>
      <text x={W / 2} y="228" fill="#5d7891" fontSize="9" fontFamily="monospace" textAnchor="middle" letterSpacing="0.05em">diffusion vocoder → waveform</text>

      <line x1="20" y1="300" x2={W - 20} y2="300" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
      <path ref={outPathRef} fill="none" stroke="#5cf0c8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
